import posthog from 'posthog-js';

const POSTHOG_KEY = 'phc_D1pHJaFVBlw0mWwr25NNmy2GfDtqx8LYRs8vBSsMCbk';
// POSTHOG_HOST is env-configurable so cellular-gateway deployments can
// point at an India-hosted reverse proxy (e.g. analytics.myhub.co.in)
// that forwards to PostHog cloud. Indian carriers inject TCP RSTs on
// traffic to us.i.posthog.com — proxying through our own SNI/domain
// avoids the RST and keeps the cellular leg entirely within India-to-India
// (the final proxy→PostHog hop runs from EC2, not under cellular carrier
// inspection).
//
// Build-time override: set REACT_APP_POSTHOG_HOST=https://analytics.myhub.co.in
// before `npm run build`. If unset we default to PostHog cloud US, which
// is fine for non-kiosk web traffic (direct ISP routes don't get RST'd).
const POSTHOG_HOST =
  process.env.REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com';

// PostHog is disabled in kiosk WebView contexts:
//   1. Kiosks typically run through a cellular gateway where every
//      third-party request costs real money and (empirically) many of
//      them fail with ERR_CONNECTION_RESET due to cellular MTU /
//      PPP fragmentation on TLS ClientHello packets. The PostHog client
//      then retries up to 10 times per failed event, which burns even
//      more cellular data for zero telemetry benefit.
//   2. Kiosk sessions are anonymous service-desk users. PostHog user
//      identification is meaningless in that context.
// Detection uses the Android WebView marker in the User-Agent (" wv)")
// which is set by all Android WebView-based apps including our
// com.myhub.kiosk app. Regular Chrome mobile users do NOT have this
// marker, so analytics continues to work on every other surface.
// An explicit env var (REACT_APP_ANALYTICS_DISABLED=true) provides
// an override escape hatch for any future deployment that needs it.
const isKioskWebView = () => {
  if (typeof navigator === 'undefined') return false;
  return / wv\)/.test(navigator.userAgent);
};

const ANALYTICS_DISABLED =
  process.env.REACT_APP_ANALYTICS_DISABLED === 'true' || isKioskWebView();

let initialized = false;

export const initAnalytics = () => {
  if (initialized) return;

  if (ANALYTICS_DISABLED) {
    // Don't call posthog.init(). The track/identify helpers below
    // check ANALYTICS_DISABLED explicitly as their first guard, so
    // they no-op on uninitialized PostHog without relying on
    // posthog-js's internal pre-init queueing (which is fragile and
    // undocumented). `initialized` stays false here on purpose —
    // the module-level re-entry guard (`if (initialized) return`) at
    // the top of this function still works because initAnalytics()
    // is wired through React's useEffect once in index.js.
    // eslint-disable-next-line no-console
    console.log('[analytics] disabled (kiosk WebView or env override)');
    return;
  }

  // Lean PostHog config: explicit events only, no background chatter.
  //
  // The default config PostHog ships with is chatty: it fires on every
  // pageview, every pageleave, every click (autocapture), polls /decide
  // and /flags periodically for feature flags, and loads extra scripts
  // for dead-clicks and session recording. HAR capture from a real kiosk
  // session showed 6 PostHog requests eating 9.2 seconds of wall time
  // for ONE feedback submission, vs the actual submitFeedback call which
  // took 173 ms. The 6 requests were background polling — not the
  // meaningful `ratingSelected` / `feedbackSubmitted` events we actually
  // care about.
  //
  // Everything here is turned off EXCEPT explicit `posthog.capture()`
  // calls, which is what `trackEvent()` and the `analytics.*` helpers
  // below use. When the user submits feedback, we still get a
  // `feedback_submitted` event; we just don't get a pageview event
  // from opening the app, a pageleave from closing it, or 5 auto-
  // captured taps along the way.
  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    capture_pageview: false,
    capture_pageleave: false,
    autocapture: false,
    disable_session_recording: true,
    advanced_disable_decide: true,
    // capture_heatmaps defaults to TRUE even with autocapture off. The
    // SDK attaches a global mousemove listener and POSTs synthetic
    // `$$heatmap` events with coordinates to /e/. On kiosks (touch, no
    // mouse) and on any low-signal web session, these are noise —
    // dominant entries in our HAR captures were `$$heatmap` POSTs that
    // burned 5-22s each on cellular gateways. Kill it explicitly.
    capture_heatmaps: false,
    // Disable features we don't use — each eliminates an /e/ POST or
    // a /decide/ roundtrip and shrinks the SDK's background chatter.
    disable_surveys: true,
    disable_web_experiments: true,
    enable_recording_console_log: false,
    persistence: 'localStorage',
  });

  initialized = true;
};

// Track custom events. Two guards:
//   1. ANALYTICS_DISABLED — set at module load from the kiosk-WebView UA
//      sniff and the REACT_APP_ANALYTICS_DISABLED env var. Never changes
//      after module init, so a straight boolean check is sufficient and
//      doesn't depend on initAnalytics() having run.
//   2. initialized — guards the period between module load and when
//      initAnalytics() finishes calling posthog.init(). Without this,
//      posthog.capture() could be invoked on a PostHog instance that
//      has no api_host yet, which silently queues the event in a fragile
//      in-memory buffer.
export const trackEvent = (eventName, properties = {}) => {
  if (ANALYTICS_DISABLED || !initialized) return;
  posthog.capture(eventName, properties);
};

// Identify user/device
export const identifyDevice = (deviceId, properties = {}) => {
  if (ANALYTICS_DISABLED || !initialized) return;
  posthog.identify(deviceId, properties);
};

// Set user properties
export const setUserProperties = (properties) => {
  if (ANALYTICS_DISABLED || !initialized) return;
  posthog.people.set(properties);
};

// Pre-defined events for feedback app
export const analytics = {
  // Page/session events
  pageView: (page, properties = {}) => {
    trackEvent('page_view', { page, ...properties });
  },

  // Feedback events
  ratingSelected: (rating, properties = {}) => {
    trackEvent('rating_selected', { rating, ...properties });
  },

  reasonSelected: (reason, rating, properties = {}) => {
    trackEvent('reason_selected', { reason, rating, ...properties });
  },

  feedbackSubmitted: (data) => {
    trackEvent('feedback_submitted', {
      rating: data.rating,
      reasons: data.reasons,
      hasComment: !!data.additionalComment,
      washroomId: data.washroomId,
      refId: data.refId,
    });
  },

  feedbackReset: () => {
    trackEvent('feedback_reset');
  },

  // Task management events
  staffLoginClicked: () => {
    trackEvent('staff_login_clicked');
  },

  staffLoginSuccess: (staffId) => {
    trackEvent('staff_login_success', { staffId });
  },

  taskViewed: (taskId) => {
    trackEvent('task_viewed', { taskId });
  },

  taskResolved: (taskId, resolution) => {
    trackEvent('task_resolved', { taskId, resolution });
  },

  // Error events
  errorOccurred: (errorCode, errorMessage) => {
    trackEvent('error_occurred', { errorCode, errorMessage });
  },

  // Device context
  setDeviceContext: (deviceId, location, isKiosk = false) => {
    identifyDevice(deviceId, {
      location,
      isKiosk,
      platform: isKiosk ? 'kiosk' : 'web',
    });
  },
};

export default posthog;
